import type { KeyCombo } from '@/shared/lib/keyCombo';

/**
 * Central keybinding configuration — the single source of truth for every
 * keyboard shortcut in the app (VS Code's command/keybinding split). Each entry
 * maps a stable command id to its keybinding; features bind a *handler* to a
 * command id via `registerShortcut`/`useRegisterShortcut` and never specify keys
 * at the call site, so remapping a shortcut is a one-line edit here.
 *
 * The set of valid command ids is derived from this table ({@link ShortcutId}),
 * so a hotkey cannot be bound unless it is declared here. Escape is deliberately
 * NOT configured — the Radix dialog primitive owns Escape-to-close and never
 * routes through this registry.
 */

/**
 * Shortcut scopes, highest-priority last. The `<ShortcutHost/>` fires the single
 * highest-scope binding for a pressed combo, so a `dialog`-scope shortcut
 * shadows a `global` one for the *same* combo while it is registered (i.e. while
 * the dialog is open). Start minimal; add tiers here as new surfaces (a focused
 * editor panel, a command palette, …) need to shadow lower ones.
 */
export type ShortcutScope = 'global' | 'dialog';

/** Numeric priority per scope (higher wins). Ties break toward the most recent registration. */
export const SCOPE_PRIORITY: Record<ShortcutScope, number> = {
  global: 0,
  dialog: 100,
};

/** One command's keybinding. `scope` defaults to `global` when omitted. */
export interface KeybindingConfig {
  /** Key combo that triggers the command, e.g. `'mod+b'` (`mod` = ⌘ on macOS / Ctrl elsewhere). */
  keys: KeyCombo;
  /** Resolution tier; higher scopes shadow lower ones for the same combo. Defaults to `global`. */
  scope?: ShortcutScope;
  /** Call `event.preventDefault()` when this command wins the combo. */
  preventDefault?: boolean;
  /** Fire even when the keydown originates from an editable element (default: suppressed). */
  allowInInput?: boolean;
}

/** The central keybinding table. Add a shortcut by adding an entry here. */
export const SHORTCUTS = {
  'sidebar.toggle': {
    keys: 'mod+b',
    scope: 'global',
    // A modified combo is safe to fire while a field is focused, and we suppress
    // the browser's default so ⌘/Ctrl-B never bolds text in an input.
    preventDefault: true,
    allowInInput: true,
  },
  // MCAP timeline segment authoring. Bare single keys: suppressed while an input
  // (e.g. an exact-time field) is focused, so `allowInInput` is left off.
  'segment.markStart': {
    keys: 'j',
    scope: 'global',
  },
  'segment.markEnd': {
    keys: 'k',
    scope: 'global',
  },
  'timeline.zoomIn': {
    keys: '=',
    scope: 'global',
  },
  'timeline.zoomOut': {
    keys: '-',
    scope: 'global',
  },
  'timeline.loop': {
    keys: 'l',
    scope: 'global',
  },
  // Submit all pending segment drafts (mirrors the v1 Annotator Editor's Shift+Enter).
  // A modified combo, so it is safe to fire while a field is focused.
  'segment.submit': {
    keys: 'shift+enter',
    scope: 'global',
    preventDefault: true,
    allowInInput: true,
  },
  // Video playback transport (the HLS asset viewer). All bare keys, so they stay
  // suppressed while the inspector's metadata fields are focused. The four that the
  // browser also acts on suppress the default: Space scrolls (or re-activates the
  // focused button), the arrows scroll, and — because the `<video>` now carries native
  // `controls` and can hold focus — Space/arrows/M are also the element's own
  // bindings, which would otherwise fire alongside ours and cancel the toggle out.
  'video.playPause': {
    keys: 'space',
    scope: 'global',
    preventDefault: true,
  },
  'video.skipBack': {
    keys: 'arrowleft',
    scope: 'global',
    preventDefault: true,
  },
  'video.skipForward': {
    keys: 'arrowright',
    scope: 'global',
    preventDefault: true,
  },
  // Single-frame stepping, matching the v1 video player's `,` / `.` bindings.
  'video.frameBack': {
    keys: ',',
    scope: 'global',
  },
  'video.frameForward': {
    keys: '.',
    scope: 'global',
  },
  'video.muteToggle': {
    keys: 'm',
    scope: 'global',
    preventDefault: true,
  },
} satisfies Record<string, KeybindingConfig>;

/** Every valid command id — derived from the central table. */
export type ShortcutId = keyof typeof SHORTCUTS;

const EDITABLE_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

/**
 * True when the event target is an editable element (input/textarea/select/
 * contenteditable). The host uses this to suppress shortcuts while the user is
 * typing, unless the winning command sets `allowInInput`.
 */
export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return EDITABLE_TAGS.has(target.tagName) || target.isContentEditable;
}
